/**
 * AI Traffic Tracker - GA4 Integration
 *
 * Detects AI platform referrals (ChatGPT, Claude, Perplexity, Google AI, etc.)
 * and sends custom tracking events to Google Analytics 4.
 *
 * Usage:
 *   <script src="{{ 'ai-traffic-tracker.js' | asset_url }}" defer></script>
 *
 * Automatically initializes on DOMContentLoaded.
 *
 * @version 1.0.0
 * @author Claude Code Assistant
 * @date 2025-11-05
 */

(function() {
  'use strict';

  /**
   * AI Traffic Tracker Class
   */
  class AITrafficTracker {
    constructor() {
      this.aiPlatforms = {
        'chat.openai.com': 'chatgpt',
        'chatgpt.com': 'chatgpt',
        'claude.ai': 'claude',
        'anthropic.com': 'claude',
        'perplexity.ai': 'perplexity',
        'gemini.google.com': 'google_ai',
        'bing.com/chat': 'bing_copilot',
        'you.com': 'you_ai'
      };

      this.aiUtmSources = [
        'chatgpt',
        'claude',
        'perplexity',
        'google_ai',
        'gemini',
        'bing_copilot',
        'you_ai'
      ];

      this.sessionKey = 'ai_traffic_source';
    }

    /**
     * Detect AI platform from document.referrer
     * @param {string} referrer - The document.referrer value
     * @returns {string|null} AI platform name or null
     */
    detectAISource(referrer) {
      if (!referrer || typeof referrer !== 'string' || referrer.trim() === '') {
        return null;
      }

      try {
        const lowerReferrer = referrer.toLowerCase();

        // Check each known AI platform
        for (const [domain, platform] of Object.entries(this.aiPlatforms)) {
          if (lowerReferrer.includes(domain.toLowerCase())) {
            return platform;
          }
        }

        return null;
      } catch (error) {
        console.warn('[AI Tracker] Error detecting AI source from referrer:', error);
        return null;
      }
    }

    /**
     * Detect AI platform from UTM parameters
     * @param {string} url - Current page URL with UTM parameters
     * @returns {string|null} AI platform name or null
     */
    detectAISourceFromUTM(url) {
      if (!url || typeof url !== 'string') {
        return null;
      }

      try {
        const urlObj = new URL(url);
        const utmSource = urlObj.searchParams.get('utm_source');
        const utmMedium = urlObj.searchParams.get('utm_medium');

        // Check if utm_source matches known AI platforms
        if (utmSource && this.aiUtmSources.includes(utmSource.toLowerCase())) {
          return utmSource.toLowerCase();
        }

        // Check if utm_medium=ai with specific source
        if (utmMedium && utmMedium.toLowerCase() === 'ai') {
          if (utmSource === 'google') {
            return 'google_ai';
          }
          // Return the source with _ai suffix if it's a recognized pattern
          if (utmSource) {
            return utmSource.toLowerCase();
          }
        }

        return null;
      } catch (error) {
        console.warn('[AI Tracker] Error detecting AI source from UTM:', error);
        return null;
      }
    }

    /**
     * Get device type from user agent
     * @param {string} userAgent - Navigator.userAgent value
     * @returns {'mobile'|'desktop'|'tablet'} Device type
     */
    getDeviceType(userAgent) {
      if (!userAgent || typeof userAgent !== 'string') {
        return 'desktop';
      }

      const ua = userAgent.toLowerCase();

      // Check for tablet first (iPad)
      if (ua.includes('ipad')) {
        return 'tablet';
      }

      // Check for Android (must check mobile before tablet)
      if (ua.includes('android')) {
        // Android phone has "mobile" in UA, tablet doesn't
        if (ua.includes('mobile')) {
          return 'mobile';
        } else {
          return 'tablet';
        }
      }

      // Check for other mobile devices
      if (ua.includes('iphone') ||
          ua.includes('mobile') ||
          ua.includes('webos') ||
          ua.includes('blackberry')) {
        return 'mobile';
      }

      return 'desktop';
    }

    /**
     * Create tracking event data for GA4
     * @param {string} source - AI platform name
     * @param {'organic'|'referral'|'direct'} queryType - Type of query
     * @param {'mobile'|'desktop'|'tablet'} device - Device type
     * @returns {Object} GA4 event data object
     */
    createTrackingEvent(source, queryType, device) {
      // Validation
      if (!source || typeof source !== 'string' || source.trim() === '') {
        throw new Error('ai_source cannot be empty');
      }

      const validQueryTypes = ['organic', 'referral', 'direct'];
      if (!validQueryTypes.includes(queryType)) {
        throw new Error(`Invalid query_type: ${queryType}. Must be one of: ${validQueryTypes.join(', ')}`);
      }

      // Check if this is the first AI visit in session
      const isSessionStart = !this.getStoredAISource();

      return {
        event: 'ai_traffic',
        ai_source: source,
        ai_query_type: queryType,
        ai_device: device,
        ai_session_start: isSessionStart
      };
    }

    /**
     * Send tracking event to GA4
     * @param {Object} event - Event data
     */
    sendToGA4(event) {
      // Check if gtag is available
      if (typeof gtag === 'undefined' || typeof gtag !== 'function') {
        console.warn('[AI Tracker] gtag is not available, skipping event send');
        return;
      }

      try {
        // Send event to GA4
        gtag('event', event.event, {
          ai_source: event.ai_source,
          ai_query_type: event.ai_query_type,
          ai_device: event.ai_device,
          ai_session_start: event.ai_session_start
        });

        console.log('[AI Tracker] Event sent to GA4:', event);
      } catch (error) {
        console.error('[AI Tracker] Error sending event to GA4:', error);
      }
    }

    /**
     * Get stored AI source from sessionStorage
     * @returns {string|null}
     */
    getStoredAISource() {
      if (typeof sessionStorage === 'undefined') {
        return null;
      }

      try {
        return sessionStorage.getItem(this.sessionKey);
      } catch (error) {
        console.warn('[AI Tracker] Error reading sessionStorage:', error);
        return null;
      }
    }

    /**
     * Store AI source in sessionStorage
     * @param {string} source
     */
    storeAISource(source) {
      if (typeof sessionStorage === 'undefined') {
        return;
      }

      try {
        sessionStorage.setItem(this.sessionKey, source);
      } catch (error) {
        console.warn('[AI Tracker] Error writing to sessionStorage:', error);
      }
    }

    /**
     * Initialize tracker with current page context
     */
    initialize() {
      // Server-side rendering safety check
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }

      // Check if we already tracked AI traffic in this session
      const storedSource = this.getStoredAISource();
      if (storedSource) {
        console.log('[AI Tracker] AI traffic already tracked in this session:', storedSource);
        return;
      }

      let aiSource = null;
      let queryType = 'referral';

      // Priority 1: Check UTM parameters (most reliable)
      try {
        aiSource = this.detectAISourceFromUTM(window.location.href);
        if (aiSource) {
          queryType = 'organic'; // UTM parameters usually indicate organic traffic
        }
      } catch (error) {
        console.warn('[AI Tracker] Error checking UTM parameters:', error);
      }

      // Priority 2: Check referrer if no UTM match
      if (!aiSource) {
        try {
          aiSource = this.detectAISource(document.referrer);
          if (aiSource) {
            queryType = 'referral';
          }
        } catch (error) {
          console.warn('[AI Tracker] Error checking referrer:', error);
        }
      }

      // If AI source detected, track it
      if (aiSource) {
        const device = this.getDeviceType(navigator.userAgent);
        const event = this.createTrackingEvent(aiSource, queryType, device);

        // Send to GA4
        this.sendToGA4(event);

        // Store in session to prevent duplicate tracking
        this.storeAISource(aiSource);
      } else {
        console.log('[AI Tracker] No AI referral detected');
      }
    }
  }

  // Auto-initialize when DOM is ready
  if (typeof window !== 'undefined') {
    // Create global instance
    window.aiTrafficTracker = new AITrafficTracker();

    // Initialize on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        window.aiTrafficTracker.initialize();
      });
    } else {
      // DOM already loaded
      window.aiTrafficTracker.initialize();
    }
  }

  // Export for testing (Node.js environment)
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = AITrafficTracker;
  }
})();
